import { io, Socket } from 'socket.io-client';

// Import types from server
export type {
  TransactionUpdateEvent,
  PaymentEvent,
  DeviceApprovalRequestEvent,
  DeviceResponseEvent,
  BalanceUpdateEvent,
  KycStatusUpdateEvent,
  SocketEventName,
  SocketEventMap,
} from '~/types/socket';
import { getSessionToken } from '~/hooks/useAuth';

// Keep a single socket instance across HMR and route changes
declare global {
  // eslint-disable-next-line no-var
  var __wafraSocket__: {
    client: Socket | null;
    token: string | null;
    initialized: boolean;
  } | undefined;
}

const getGlobalSocketContainer = () => {
  if (!globalThis.__wafraSocket__) {
    globalThis.__wafraSocket__ = {
      client: null,
      token: null,
      initialized: false,
    };
  }
  return globalThis.__wafraSocket__;
};

export const getSocket: () => Promise<Socket> = async () => {
  const token = getSessionToken();
  const container = getGlobalSocketContainer();

  // Only create socket once, regardless of how many times getSocket is called
  if (!container.client && !container.initialized) {
    if (!token) {
      throw new Error('No authentication token available');
    }

    container.initialized = true;
    container.token = token;

    // Use the same URL as the API calls for consistency
    const serverUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

    console.log('🔌 Creating single socket instance for:', serverUrl);

    container.client = io(serverUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 10000,
      forceNew: false,
      auth: {
        token: token,
      },
    });

    container.client.on('connect', () => {
      console.log('🔌 Socket connected to:', serverUrl);
    });

    container.client.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
    });

    container.client.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
    });

    container.client.on('reconnect', (attemptNumber) => {
      console.log('🔌 Socket reconnected after', attemptNumber, 'attempts');
    });

    container.client.on('reconnect_error', (error) => {
      console.error('🔌 Socket reconnection error:', error);
    });

    // Test message listener for debugging
    container.client.on('test-message', (data) => {
      console.log('🔌 Received test message:', data);
    });
  }
  return container.client as Socket;
};

// Simple room management
export const joinUserRoom = async (userId: string) => {
  const socket = await getSocket();
  socket.emit('join', `user-${userId}`);
};

export const leaveUserRoom = async (userId: string) => {
  const token = getSessionToken();
  if (!token) {
    return; // TODO
  }
  const socket = await getSocket();
  socket.emit('leave', `user-${userId}`);
};

// Re-export event constants from local types
export { SOCKET_EVENTS as EVENTS } from '~/types/socket';

// Cleanup function
export const disconnectSocket = () => {
  const container = getGlobalSocketContainer();
  if (container.client) {
    container.client.disconnect();
    container.client = null;
  }
  container.initialized = false;
  container.token = null;
};
